import React from 'react';
import {
  NavigableSwitch,
  Route,
  Failover,
} from 'Components/UI/FacetRouter/FacetRouter';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import { QueryVal } from 'Helpers/Query';
import Expense from 'Components/Universal/Questionnaire/Screens/Expense';
import Liabilities from 'Components/Universal/Questionnaire/Screens/Liabilities';
import PersonalInformation from 'Components/Universal/Questionnaire/Screens/PersonalInformation';
import FinancialProfile from 'Components/Universal/Questionnaire/Screens/FinancialProfile';
import FamilyInformation from 'Components/Universal/Questionnaire/Screens/FamilyInformation';
import Goals from 'Components/Universal/Questionnaire/Screens/Goals';
import OtherAdvisors from 'Components/Universal/Questionnaire/Screens/OtherAdvisors';
import AdditionalNotes from 'Components/Universal/Questionnaire/Screens/AdditionalNotes';
import EstatePlanning from 'Components/Universal/Questionnaire/Screens/EstatePlanning';
import SSIAndPensions from 'Components/Universal/Questionnaire/Screens/SSIAndPensions';
import Insurance from 'Components/Universal/Questionnaire/Screens/Insurance';
import IncomeDetails from 'Components/Universal/Questionnaire/Screens/IncomeDetails';
import ExtraAreaContext from 'Components/Universal/Questionnaire/ExtraAreaContext';
import {
  personalInformation,
  familyInformation,
  goals,
  incomeDetails,
  ssiAndPensions,
  liabilities,
  insurance,
  estatePlanning,
  financialProfile,
  otherAdvisors,
  expense,
  additionalNotes,
} from 'Components/Universal/Questionnaire/RoutePaths';
import {
  householdEditRecordAdditionalNotesUri,
  householdEditRecordEstatePlanningUri,
  householdEditRecordExpenseUri,
  householdEditRecordFamilyInformationUri,
  householdEditRecordFinancialProfileUri,
  householdEditRecordGoalsUri,
  householdEditRecordIncomeDetailsUri,
  householdEditRecordInsuranceUri,
  householdEditRecordLiabilitiesUri,
  householdEditRecordOtherAdvisorsUri,
  householdEditRecordPersonalInformationUri,
  householdEditRecordSsiAndPensionsUri,
} from 'Consts/advisorUris';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import fr from 'facet-rest';
import NoteContainer from './Section/NoteContainer';

const notes = sectionId => <NoteContainer sectionId={sectionId} />;

const getQuestionnaireRoutes = ((householdId, householdResource) => {
  const goalsRoute = householdResource.withRelationship(fr.relationships.goalsPage.name,
    () => [],
    () => [
      <Route
        path={goals}
        component={Goals}
        title="Goals"
        test="goals"
        href={householdEditRecordGoalsUri(householdId)}
      />,
    ]) || [];
  const allRoutes = [
    <Route
      path={personalInformation}
      component={PersonalInformation}
      title="Personal Information"
      test="personal"
      href={householdEditRecordPersonalInformationUri(householdId)}
    />,
    <Route
      path={familyInformation}
      component={FamilyInformation}
      title="Family Information"
      test="family"
      href={householdEditRecordFamilyInformationUri(householdId)}
    />,
    ...goalsRoute,
    <Route
      path={incomeDetails}
      component={IncomeDetails}
      title="Income Details"
      test="income"
      href={householdEditRecordIncomeDetailsUri(householdId)}
    />,
    <Route
      path={ssiAndPensions}
      component={SSIAndPensions}
      title="SSI & Pensions"
      test="ssi"
      href={householdEditRecordSsiAndPensionsUri(householdId)}
    />,
    <Route
      path={liabilities}
      component={Liabilities}
      title="Credit Score"
      test="liabilities"
      href={householdEditRecordLiabilitiesUri(householdId)}
    />,
    <Route
      path={insurance}
      component={Insurance}
      title="Insurance"
      test="insurance"
      href={householdEditRecordInsuranceUri(householdId)}
    />,
    <Route
      path={estatePlanning}
      component={EstatePlanning}
      title="Estate Planning"
      test="estate"
      href={householdEditRecordEstatePlanningUri(householdId)}
    />,
    <Route
      path={financialProfile}
      component={FinancialProfile}
      title="Financial Profile"
      test="financial"
      href={householdEditRecordFinancialProfileUri(householdId)}
    />,
    <Route
      path={otherAdvisors}
      component={OtherAdvisors}
      title="Other Advisors"
      test="other"
      href={householdEditRecordOtherAdvisorsUri(householdId)}
    />,
    <Route
      path={expense}
      component={Expense}
      title="Expenses"
      test="expenses"
      href={householdEditRecordExpenseUri(householdId)}
    />,
    <Route
      path={additionalNotes}
      component={AdditionalNotes}
      title="Additional Notes"
      test="additional"
      href={householdEditRecordAdditionalNotesUri(householdId)}
    />,
  ];

  return allRoutes;
});

const Questionnaire = () => {
  const { householdResource } = useSimpleHousehold();
  const householdId = QueryVal('householdId');
  const { rootNodeProps } = useEventTrackingLogEvent({
    nodeName: 'Questionnaire',
    isPageView: true,
  });

  if (!householdId || householdId === '0' || !householdResource) {
    return <div>No householdId defined</div>;
  }

  return (
    <div {...rootNodeProps} test="questionnaire-page">
      <ExtraAreaContext.Provider value={notes}>
        <NavigableSwitch>
          {getQuestionnaireRoutes(householdId, householdResource)}
          <Failover to={householdEditRecordPersonalInformationUri(householdId)} />
        </NavigableSwitch>
      </ExtraAreaContext.Provider>
    </div>
  );
};

export default Questionnaire;
